import { datadogRum } from '@datadog/browser-rum';
import trackError from 'shared/helpers/errorTracking';
import React, {
  createContext, useReducer, useContext, useEffect, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { useAuth, useSigninCheck } from 'reactfire';
import { useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { ME } from 'shared/queries/account/queries';
import LinearProgressCustom from 'shared/components/LinearProgressCustom';
import { Crisp } from 'crisp-sdk-web';
import {
  openSelectRole, openSignIn, openSignUp, openHasConfirmRotation,
  closeAll,
} from 'actions/authDialogActions';
import SNACKBAR_OPEN from 'actions/snackbarAction';
import paths from '../paths';

const SET_USER = 'SET_USER';
const userData = { user: {}, called: false };
const UserContext = createContext({});

const shouldInitMonitoring = ['staging', 'prod'].includes(process.env.REACT_APP_TARGET_ENV);

if (shouldInitMonitoring) {
  const datadogApplicationId = process.env.REACT_APP_DATADOG_APPLICATION_ID;
  const datadogClientToken = process.env.REACT_APP_DATADOG_CLIENT_TOKEN;

  if (datadogApplicationId && datadogClientToken) {
    datadogRum.init({
      applicationId: datadogApplicationId,
      clientToken: datadogClientToken,
      site: 'datadoghq.com',
      service: 'preceptorlink-frontend',
      env: process.env.REACT_APP_TARGET_ENV,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 0,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'allow',
    });
  }
}

const userStateReducer = (state, action) => {
  if (action.type === SET_USER) {
    return { ...state, ...action.payload };
  }
  return state;
};

/**
 * @param children
 * @returns {JSX.Element}
 *
 * Stores PreceptorLink user
 */
export const UserProvider = ({ children }) => {
  const reduxDispatch = useDispatch();
  const { status, data: signInCheckResult } = useSigninCheck();
  const [state, dispatch] = useReducer(userStateReducer, userData);
  const location = useLocation();
  const history = useHistory();
  const locationChangeRef = useRef(0);
  const auth = useAuth();

  const { loading, data: { me } = {}, error } = useQuery(ME, {
    fetchPolicy: 'network-only', skip: !signInCheckResult?.signedIn,
  });

  useEffect(() => {
    Crisp.configure(process.env.REACT_APP_CRISP_WEBSITE_ID);
  }, []);

  const handleClose = () => {
    reduxDispatch(closeAll());
  };

  useEffect(() => {
    if (error && error.message.includes('already exists')) {
      reduxDispatch({
        type: SNACKBAR_OPEN,
        open: true,
        variant: 'alert',
        alertSeverity: 'error',
        message: 'This email is already registered.',
      });
      setTimeout(() => {
        auth.signOut().then(() => {
          handleClose();
          dispatch(openSignIn());
        }).catch(signOutError => {
          trackError(signOutError);
          handleClose();
          dispatch(openSignIn());
        });
      }, 100);
    }
  // eslint-disable-next-line
  }, [error]);

  useEffect(() => {
    if (me?.email) {
      Crisp.user.setEmail(me?.email, me?.crispHmac);
    }
    if (me?.getName) {
      Crisp.user.setNickname(me?.getName);
    }
  }, [me?.email, me?.crispHmac, me?.getName]);

  useEffect(() => {
    if (me?.hasRole === false) {
      history.push(paths.wizardPaths.welcome.reverse());
      setTimeout(() => {
        reduxDispatch(openSelectRole());
      }, 100);
    }
  }, [me, reduxDispatch, history]);

  useEffect(() => {
    dispatch({ type: SET_USER, payload: { user: me, called: status !== 'loading' && !loading } });
  }, [me, status, loading]);

  useEffect(() => {
    if (shouldInitMonitoring && signInCheckResult?.signedIn) {
      const email = signInCheckResult?.user?.email;
      datadogRum.setUser({ email });
    }
  }, [signInCheckResult?.signedIn, signInCheckResult?.user?.email]);

  const { signIn, signUp } = paths.accountPaths;
  const { verifyEmail } = paths.commonPaths;

  useEffect(() => {
    if (
      !signInCheckResult?.signedIn
      && locationChangeRef.current === 2
      && ![signIn.pattern, signUp.pattern, verifyEmail.pattern].includes(location.pathname)
    ) {
      const isLogin = localStorage.getItem('isLogin');
      if (isLogin) {
        reduxDispatch(openSignIn());
      } else {
        reduxDispatch(openSignUp());
      }
      localStorage.setItem('signUpDialogTime', new Date().getTime().toString());
    }
    locationChangeRef.current += 1;
  }, [location, reduxDispatch, signInCheckResult?.signedIn, signIn, signUp, verifyEmail]);

  useEffect(() => {
    const checkSignUpDialog = () => {
      const signUpDialogTime = localStorage.getItem('signUpDialogTime');
      const currentTime = new Date().getTime();
      // 10 minutes
      if (!signInCheckResult?.signedIn && (!signUpDialogTime || currentTime - signUpDialogTime > 600000)
        && ![signIn.pattern, signUp.pattern, verifyEmail.pattern].includes(location.pathname)) {
        const isLogin = localStorage.getItem('isLogin');
        if (isLogin) {
          reduxDispatch(openSignIn());
        } else {
          reduxDispatch(openSignUp());
        }
        localStorage.setItem('signUpDialogTime', currentTime.toString());
      }
    };

    const intervalId = setInterval(checkSignUpDialog, 60000); // check every minute

    return () => clearInterval(intervalId); // cleanup on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduxDispatch, signInCheckResult?.signedIn]);

  useEffect(() => {
    if (signInCheckResult?.signedIn) {
      reduxDispatch(openHasConfirmRotation());
      const interval = setInterval(() => {
        reduxDispatch(openHasConfirmRotation());
      }, 300000); // 5 minutes
      return () => clearInterval(interval);
    }
    return () => {
      reduxDispatch(closeAll());
    };
  }, [signInCheckResult?.signedIn, reduxDispatch]);

  if (status === 'loading') return <LinearProgressCustom />;

  return (
    <UserContext.Provider value={{ state, dispatch }}>
      {children}
    </UserContext.Provider>
  );
};

UserProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useBackendUserState = () => useContext(UserContext);
